/**
 * Utility functions for time formatting and relative time calculations
 */

/**
 * Format a date to a relative time string (e.g., "vor 10 Minuten", "vor 2 Stunden")
 * @param {string|Date} dateString - The date to format
 * @returns {string} - Relative time string in German
 */
export function getRelativeTime(dateString) {
  if (!dateString) return 'Nie'
  
  const date = new Date(dateString)
  const now = new Date()
  const diffInSeconds = Math.floor((now - date) / 1000)
  
  if (diffInSeconds < 60) {
    return 'Gerade eben'
  }
  
  const diffInMinutes = Math.floor(diffInSeconds / 60)
  if (diffInMinutes < 60) {
    return `vor ${diffInMinutes} Minute${diffInMinutes === 1 ? '' : 'n'}`
  }
  
  const diffInHours = Math.floor(diffInMinutes / 60)
  if (diffInHours < 24) {
    return `vor ${diffInHours} Stunde${diffInHours === 1 ? '' : 'n'}`
  }
  
  const diffInDays = Math.floor(diffInHours / 24)
  if (diffInDays < 7) {
    return `vor ${diffInDays} Tag${diffInDays === 1 ? '' : 'en'}`
  }
  
  const diffInWeeks = Math.floor(diffInDays / 7)
  if (diffInWeeks < 4) {
    return `vor ${diffInWeeks} Woche${diffInWeeks === 1 ? '' : 'n'}`
  }
  
  const diffInMonths = Math.floor(diffInDays / 30)
  if (diffInMonths < 12) {
    return `vor ${diffInMonths} Monat${diffInMonths === 1 ? '' : 'en'}`
  }
  
  const diffInYears = Math.floor(diffInDays / 365)
  return `vor ${diffInYears} Jahr${diffInYears === 1 ? '' : 'en'}`
}

/**
 * Format a date to a detailed timestamp string
 * @param {string|Date} dateString - The date to format
 * @returns {string} - Detailed timestamp string in German
 */
export function getDetailedTimestamp(dateString) {
  if (!dateString) return 'Nie'
  
  const date = new Date(dateString)
  return date.toLocaleString('de-DE', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  })
}

/**
 * Get a tooltip text for time display
 * @param {string|Date} dateString - The date to format
 * @returns {string} - Tooltip text
 */
export function getTimeTooltip(dateString) {
  if (!dateString) return 'Keine Aktivität'
  
  const relative = getRelativeTime(dateString)
  const detailed = getDetailedTimestamp(dateString)
  
  return `${relative}\n\nDetailliert: ${detailed}`
}
